import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { EventListItem } from '../../types/content';
import { canWriteEvents, isOwner } from '../../utils/roles';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/UI/Button';
import { confirmAction } from '../../utils/confirmAction';

const MyEventsPage: React.FC = () => {
  const { user } = useAuth();
  const owner = isOwner(user);
  const [events, setEvents] = useState<EventListItem[]>([]);

  useEffect(() => {
    contentApi.myEvents().then((r) => setEvents(r.data.events || [])).catch(() => undefined);
  }, []);

  if (!canWriteEvents(user)) {
    return <div className="max-w-4xl mx-auto px-4 py-10 text-gray-600">You do not have permission to manage events.</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="flex justify-between items-center gap-3">
        <h1 className="text-3xl font-bold text-gray-900">Events</h1>
        <Link to="/admin/events/new"><Button>New event</Button></Link>
      </div>
      {events.length === 0 && <p className="mt-6 text-gray-500">You have not created any events yet.</p>}
      <div className="mt-6 space-y-3">
        {events.map((item) => (
          <div key={item.id} className="rounded-xl border border-gray-200 p-4 hover:border-primary-200 bg-white flex justify-between gap-3">
            <Link to={`/admin/events/${item.id}/edit`} className="flex-1">
              <div className="flex justify-between gap-3">
                <h2 className="font-semibold text-gray-900">{item.title}</h2>
                <span className="text-sm text-gray-500">{item.status}</span>
              </div>
              <p className="text-sm text-gray-600 mt-1">{item.mode} · {item.location || item.summary || item.subtitle}</p>
            </Link>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <Button
                variant="outline"
                onClick={async () => {
                  const ok = await confirmAction({
                    title: 'Archive this event?',
                    description: `“${item.title}” will be deactivated (soft delete).`,
                    confirmLabel: 'Archive',
                    cancelLabel: 'Keep it',
                    variant: 'danger',
                  });
                  if (!ok) return;
                  contentApi.archiveEvent(item.id).then(() => setEvents((current) => current.filter((row) => row.id !== item.id)));
                }}
              >
                Archive
              </Button>
              {owner ? (
                <button
                  type="button"
                  className="text-sm text-error-700 font-medium"
                  onClick={async () => {
                    const ok = await confirmAction({
                      title: 'Delete event permanently?',
                      description: `“${item.title}” will be permanently removed. This cannot be undone.`,
                      confirmLabel: 'Delete permanently',
                      cancelLabel: 'Cancel',
                      variant: 'danger',
                    });
                    if (!ok) return;
                    contentApi.permanentDeleteEvent(item.id).then(() => setEvents((current) => current.filter((row) => row.id !== item.id)));
                  }}
                >
                  Delete permanently
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyEventsPage;
